// Generated by Claude (Claude Code). Conversation: https://claude.ai/code/session_01EL8pTjS9jd27w4Xu3veGVW

/**
 * Active-time accounting for one page mount, with no DOM or timer access so it can
 * be unit tested with a fake clock. The hook in hooks/use-active-time-tracker.ts
 * feeds it browser events.
 *
 * Time counts only while all three hold:
 *   - the tab is visible
 *   - the window has focus
 *   - the user is not idle
 *
 * Time is accumulated from timestamps, not by counting timer ticks, so throttled
 * timers in background tabs cannot inflate or shrink it.
 */
export type ActiveTimeTracker = {
  setVisible(visible: boolean): void;
  setFocused(focused: boolean): void;
  setIdle(idle: boolean): void;
  isActive(): boolean;
  /** Cumulative active milliseconds since the tracker was created. */
  activeMs(): number;
};

export function createActiveTimeTracker(
  now: () => number,
  initial: { visible: boolean; focused: boolean },
): ActiveTimeTracker {
  let visible = initial.visible;
  let focused = initial.focused;
  let idle = false;
  let accumulatedMs = 0;
  let activeSince: number | null = null;

  function shouldBeActive() {
    return visible && focused && !idle;
  }

  function sync() {
    if (shouldBeActive()) {
      if (activeSince === null) activeSince = now();
    } else if (activeSince !== null) {
      accumulatedMs += Math.max(0, now() - activeSince);
      activeSince = null;
    }
  }

  sync();

  return {
    setVisible(v) {
      visible = v;
      sync();
    },
    setFocused(f) {
      focused = f;
      sync();
    },
    setIdle(i) {
      idle = i;
      sync();
    },
    isActive() {
      return activeSince !== null;
    },
    activeMs() {
      const running = activeSince === null ? 0 : Math.max(0, now() - activeSince);
      return Math.round(accumulatedMs + running);
    },
  };
}
